import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  IAuthAction,
  IAuthResponse,
  IResetAction,
  ISystemAction,
  IValidateAction,
  IValidateResponse,
  IGoogleAuthAction,
} from 'types/session/auth';
import {
  IDomainCheck,
  IExistingWorkspace,
  IOnboarding,
  IOnboardingResponse,
  ISignUpResponse,
  ISignUpWorkspaceResponse,
} from 'types/domain/onboarding';
import { AxiosWrapper } from './axios.wrapper';
import { PasswordType } from 'app/pages/auth/Password';

export const validationRequest = createAsyncThunk('validate', async (data: IValidateAction) => {
  const response = await AxiosWrapper.post('/auth/validate', data);
  return response.data as IValidateResponse;
});

export const authRequest = createAsyncThunk('auth', async (data: IAuthAction) => {
  const response = await AxiosWrapper.post('/auth', data);
  return response.data as IAuthResponse;
});

export const authGoogleRequest = createAsyncThunk('auth/google/signin', async (data: IGoogleAuthAction) => {
  const response = await AxiosWrapper.post('/auth/google/signin', data);
  return response.data as IAuthResponse;
});

export const forgotRequest = createAsyncThunk('password/forgot', async (data: Partial<IResetAction>) => {
  const response = await AxiosWrapper.post('/auth/forgot', data);
  return response.data as IAuthResponse;
});

export const passwordRequest = createAsyncThunk('password/set', async (data: Partial<IResetAction>) => {
  const response = await AxiosWrapper.post(
    data.type === PasswordType.SETUP ? '/auth/signup/password' : '/auth/reset',
    data,
  );
  return response.data as IAuthResponse;
});

export const systemSigninRequest = createAsyncThunk('system/signin', async (data: ISystemAction) => {
  const response = await AxiosWrapper.post('/auth/system/signin', data);
  return response.data as IAuthResponse;
});
export const newSigninRequest = createAsyncThunk('new/signin', async (data: { token: string }) => {
  const response = await AxiosWrapper.post('/auth/signin/new/token', data);
  return response.data as IAuthResponse;
});

/*
 * Sign-up (design 01–04b). Email: start → code → workspace. Google: google → workspace.
 * Each step before "workspace" hands back an encrypted token for the next one.
 */
export const signUpStartRequest = createAsyncThunk(
  'signup/start',
  async (data: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    company: string;
    captchaToken: string | null;
  }) => {
    const response = await AxiosWrapper.post('/auth/signup/start', data);
    return response.data as ISignUpResponse;
  },
);

export const signUpCodeRequest = createAsyncThunk('signup/code', async (data: { token: string; code: string }) => {
  const response = await AxiosWrapper.post('/auth/signup/code', data);
  return response.data as ISignUpResponse;
});

export const signUpResendRequest = createAsyncThunk('signup/resend', async (data: { token: string }) => {
  const response = await AxiosWrapper.post('/auth/signup/resend', data);
  return response.data as ISignUpResponse;
});

export const signUpGoogleRequest = createAsyncThunk('signup/google', async (data: { credential: string }) => {
  const response = await AxiosWrapper.post('/auth/signup/google', data);
  return response.data as ISignUpResponse;
});

export const signUpWorkspaceRequest = createAsyncThunk(
  'signup/workspace',
  async (data: { token: string; company: string; domain: string }) => {
    const response = await AxiosWrapper.post('/auth/signup/workspace', data);
    return response.data as ISignUpWorkspaceResponse;
  },
);

/** Checked as the address is typed, so it lives outside the store. */
export const checkSignUpDomain = async (name: string, company: string) => {
  const response = await AxiosWrapper.get('/auth/signup/domain', { params: { name, company } });
  return response.data as IDomainCheck;
};

/** "Acme Heating already uses how.io" — checked as the work email is typed. */
export const findExistingWorkspace = async (email: string) => {
  const response = await AxiosWrapper.get('/auth/signup/existing', { params: { email } });
  return (response.data || null) as IExistingWorkspace | null;
};

/* Onboarding (design 05–08), inside the new workspace. */
export const getOnboarding = async () => {
  const response = await AxiosWrapper.get('/api/onboarding');
  return response.data as IOnboardingResponse;
};

export const saveOnboarding = async (onboarding: IOnboarding) => {
  const response = await AxiosWrapper.post('/api/onboarding', onboarding);
  return response.data as IOnboarding;
};

export const saveOnboardingBrand = async (brand: { logo: string; color: string }) => {
  await AxiosWrapper.post('/api/onboarding/brand', brand);
};

export const createOnboardingExample = async (key: string) => {
  const response = await AxiosWrapper.post('/api/onboarding/example', { key });
  return response.data as { id: number };
};
